import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import api from '@/services/api';
import { GridColumnSchema } from './grid.types';

interface FilterOption {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface FilterDataState {
  [columnKey: string]: {
    options: FilterOption[];
    loading: boolean;
    error: string | null;
    hasMore: boolean;
    page: number;
  };
}

interface UseDynamicFilterDataOptions {
  schema: GridColumnSchema[];
  searchValues: Record<string, string>;
}

export const useDynamicFilterData = ({
  schema,
  searchValues
}: UseDynamicFilterDataOptions) => {
  const [filterData, setFilterData] = useState<FilterDataState>({});
  const initializedColumnsRef = useRef<Set<string>>(new Set());
  
  // Debounce search values to avoid too many API calls
  const debouncedSearchValues = useDebounce(searchValues, 500);

  // Memoize async columns to prevent unnecessary re-initialization
  const asyncColumns = useMemo(() =>
    schema.filter(col =>
      col.filter_config?.async && col.filter_config?.api_datasource
    ), [schema]);

  // Memoize async column keys to prevent unnecessary effects
  const asyncColumnKeys = useMemo(() =>
    new Set(asyncColumns.map(col => col.key)), [asyncColumns]);

  // Initialize filter data state for columns with async filters
  useEffect(() => {
    setFilterData(prev => {
      const initialState: FilterDataState = { ...prev };
      let hasChanges = false;
      
      asyncColumns.forEach(col => {
        if (!initialState[col.key]) {
          initialState[col.key] = {
            options: [],
            loading: false,
            error: null,
            hasMore: true,
            page: 1,
          };
          hasChanges = true;
        }
      });

      // Remove filter data for columns that are no longer visible or async
      Object.keys(initialState).forEach(key => {
        if (!asyncColumnKeys.has(key)) {
          delete initialState[key];
          hasChanges = true;
        }
      });

      // Only reset initialized columns if there were actual changes
      if (hasChanges) {
        initializedColumnsRef.current.clear();
      }
      
      return hasChanges ? initialState : prev;
    });
  }, [asyncColumns, asyncColumnKeys]);

  const fetchFilterOptions = useCallback(async (
    columnKey: string, 
    column: GridColumnSchema, 
    page: number = 1, 
    search: string = ''
  ) => {
    if (!column.filter_config?.api_datasource) return;

    setFilterData(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        loading: true,
        error: null,
      }
    }));

    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      // For tree-type filters, add paginate=false to get all items
      if (column.filter_type === 'tree') {
        params.append('paginate', 'false');
      } else {
        // For non-tree filters, use pagination
        if (page > 1) params.append('page', page.toString());
        params.append('limit', '25'); // Default limit
      }

      let endpoint = column.filter_config.api_datasource;
      endpoint = endpoint.replace(/^\/?api\/v1\//, '/');

      const url = `${endpoint}?${params.toString()}`;
      const response = await api.get(url);
      
      const results = response.data?.results || response.data || [];
      const labelKey = column.filter_config.label_key || 'name';
      const valueKey = column.filter_config.value_key || 'id';

      const options: FilterOption[] = results.map((item: any) => ({
        label: item[labelKey],
        value: item[valueKey],
        ...item, // Include all original data
      }));

      setFilterData(prev => ({
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          options: page === 1 ? options : [...(prev[columnKey]?.options || []), ...options],
          loading: false,
          // For tree filters with paginate=false, there's no pagination so hasMore is always false
          hasMore: column.filter_type === 'tree' ? false : (response.data?.next ? true : false),
          page,
        }
      }));
    } catch (error) {
      console.error(`Error fetching filter options for ${columnKey}:`, error);
      setFilterData(prev => ({
        ...prev,
        [columnKey]: {
          ...prev[columnKey],
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch options',
        }
      }));
    }
  }, []);

  // Fetch initial data for async columns — runs when async columns change
  useEffect(() => {
    asyncColumns.forEach(col => {
      if (!initializedColumnsRef.current.has(col.key)) {
        initializedColumnsRef.current.add(col.key);
        fetchFilterOptions(col.key, col, 1, '');
      }
    });

    // Clean up initialized columns that are no longer visible
    const visibleColumnKeys = new Set(schema.map(col => col.key));
    const keysToRemove = Array.from(initializedColumnsRef.current).filter(key => !visibleColumnKeys.has(key));
    keysToRemove.forEach(key => {
      initializedColumnsRef.current.delete(key);
      // Clean up filter data for hidden columns
      setFilterData(prev => {
        const newData = { ...prev };
        delete newData[key];
        return newData;
      });
    });
  }, [asyncColumns, fetchFilterOptions, schema]);

  // Refetch ONLY when a specific column's debounced search value changes
  const prevDebouncedRef = useRef<Record<string, string>>({});
  useEffect(() => {
    asyncColumns.forEach(col => {
      const prev = prevDebouncedRef.current[col.key];
      const next = debouncedSearchValues[col.key];
      // Only refetch if this column's search value actually changed
      if (next !== undefined && next !== prev) {
        prevDebouncedRef.current[col.key] = next;
        fetchFilterOptions(col.key, col, 1, next);
      }
    });
  }, [asyncColumns, debouncedSearchValues, fetchFilterOptions]);

  const loadMoreOptions = useCallback((columnKey: string) => {
    const column = schema.find(col => col.key === columnKey);
    const currentData = filterData[columnKey];
    
    if (column && currentData?.hasMore && !currentData.loading) {
      fetchFilterOptions(
        columnKey, 
        column, 
        currentData.page + 1, 
        debouncedSearchValues[columnKey] || ''
      );
    }
  }, [schema, filterData, debouncedSearchValues, fetchFilterOptions]);

  const getFilterOptions = useCallback((columnKey: string): FilterOption[] => {
    return filterData[columnKey]?.options || [];
  }, [filterData]);

  const isFilterLoading = useCallback((columnKey: string): boolean => {
    return filterData[columnKey]?.loading || false;
  }, [filterData]);

  const getFilterError = useCallback((columnKey: string): string | null => {
    return filterData[columnKey]?.error || null;
  }, [filterData]);

  const hasMoreOptions = useCallback((columnKey: string): boolean => {
    return filterData[columnKey]?.hasMore || false;
  }, [filterData]);

  return {
    getFilterOptions,
    isFilterLoading,
    getFilterError,
    hasMoreOptions,
    loadMoreOptions,
    fetchFilterOptions,
  };
};